import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  RadioGroup,
  Radio,
  Stack,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const CreateABTest = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    entity_type: 'component', // Changed from 'page' to 'component'
    entity_id: '',
    goal_metric: 'conversion_rate',
    traffic_split: 50,
    variant_a_data: {},
    variant_b_data: {}
  });
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user'));
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    fetchEntities();
  }, [formData.entity_type]);

  const fetchEntities = async () => {
    try {
      let endpoint = '';
      switch (formData.entity_type) {
        case 'page_component':
          endpoint = `/page-components/${tenantId}`;
          break;
        case 'content_entry':
          endpoint = `/content-entries/${tenantId}`;
          break;
        case 'component_layout':
          endpoint = `/component-layouts/${tenantId}`;
          break;
        case 'navigation':
          endpoint = `/navigations/${tenantId}`;
          break;
        case 'page':
          endpoint = `/pages/${tenantId}`;
          break;
        default:
          return;
      }
      
      console.log('Fetching entities from:', endpoint);
      const response = await apiClient(endpoint);
      console.log('Entities response:', response);
      
      let entitiesData = [];
      
      // Handle different response formats
      if (response && response.success && Array.isArray(response.data)) {
        // Format: { success: true, data: [...] }
        entitiesData = response.data;
      } else if (response && response.status === 'success' && Array.isArray(response.data)) {
        // Format: { status: "success", data: [...] } - for content entries
        entitiesData = response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        entitiesData = response;
        
        // Handle nested array structure for page components
        if (entitiesData.length > 0 && Array.isArray(entitiesData[0])) {
          entitiesData = entitiesData.flat();
        }
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: if there's a data property
        entitiesData = response.data;
        
        // Handle nested array structure
        if (entitiesData.length > 0 && Array.isArray(entitiesData[0])) {
          entitiesData = entitiesData.flat();
        }
      } else {
        // Handle page components nested structure
        const possibleArrays = Object.values(response || {}).filter(value => Array.isArray(value));
        if (possibleArrays.length > 0) {
          entitiesData = possibleArrays.flat();
        }
      }
      
      // Filter out any invalid entries and ensure we have required properties
      entitiesData = entitiesData.filter(entity => 
        entity && 
        typeof entity === 'object' && 
        entity.id && 
        (entity.name || entity.title || entity.default_title || entity.identifier)
      );
      
      console.log('Processed entities:', entitiesData);
      setEntities(entitiesData);
      
      if (entitiesData.length === 0) {
        toast({
          title: 'No entities found',
          description: `No ${formData.entity_type} entities found for this tenant`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
      
    } catch (error) {
      console.error(`Error fetching ${formData.entity_type}s:`, error);
      toast({
        title: `Error loading ${formData.entity_type}s`,
        description: error.message || `Failed to load ${formData.entity_type}s`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setEntities([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.entity_type || !formData.entity_id) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        ...formData,
        tenant_id: tenantId,
        variant_a_data: JSON.stringify(formData.variant_a_data),
        variant_b_data: JSON.stringify(formData.variant_b_data)
      };

      console.log('Creating A/B test with payload:', payload);
      const response = await apiClient(`/ab-tests/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Create A/B test response:', response);

      // Handle different success response formats
      if (response && (response.success || response.status === 'success' || response.id)) {
        toast({
          title: 'A/B test created',
          description: response.message || 'Your A/B test has been created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        navigate('/ab-testing');
      } else {
        throw new Error(response?.message || response?.error || 'Failed to create A/B test');
      }
    } catch (error) {
      console.error('Error creating A/B test:', error);
      toast({
        title: 'Error creating A/B test',
        description: error.message || 'Failed to create A/B test',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getEntityLabel = (entityType) => {
    switch (entityType) {
      case 'page_component': return 'Page Component'; // Best for page content testing
      case 'content_entry': return 'Content Entry';   // Best for content/copy testing
      case 'component_layout': return 'Component Layout'; // For layout testing
      case 'navigation': return 'Navigation';
      case 'page': return 'Page';
      default: return entityType;
    }
  };

  // Update the entity display name function
  const getEntityDisplayName = (entity) => {
    switch (formData.entity_type) {
      case 'page_component':
        return entity.name || entity.identifier || `Component #${entity.id}`;
      case 'content_entry':
        return entity.default_title || 
               entity.title || 
               (entity.translations && entity.translations[0] && entity.translations[0].title) ||
               `Content Entry #${entity.id}`;
      case 'component_layout':
        return entity.name || `Layout #${entity.id}`;
      case 'navigation':
        return entity.name || `Navigation #${entity.id}`;
      case 'page':
        return entity.name || entity.title || `Page #${entity.id}`;
      default:
        return entity.name || entity.title || entity.identifier || `${formData.entity_type} #${entity.id}`;
    }
  };

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/ab-testing">A/B Testing</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create Test</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <Heading size="lg" mb={2}>Create A/B Test</Heading>
              <Text color="gray.500">
                Set up a new A/B test to optimize your content
              </Text>
            </Box>
            <HStack>
              <Button variant="outline" onClick={() => navigate('/ab-testing')}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                loadingText="Creating..."
              >
                Create Test
              </Button>
            </HStack>
          </HStack>

          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>Getting Started with A/B Testing</AlertTitle>
              <AlertDescription>
                A/B tests help you compare different versions of your content to see which performs better. 
                Choose what you want to test and define your success metrics.
              </AlertDescription>
            </Box>
          </Alert>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Test Configuration</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Test Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Homepage Hero Button Test"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Testing different button colors to improve conversion rate"
                    rows={3}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Test Type</FormLabel>
                  <RadioGroup
                    value={formData.entity_type}
                    onChange={(value) => setFormData({ ...formData, entity_type: value, entity_id: '' })}
                  >
                    <Stack direction="row" spacing={6}>
                      <Radio value="page">Page</Radio>
                      <Radio value="page_component">Page Component</Radio>
                      <Radio value="component_layout">Component Layout</Radio>
                      <Radio value="content_entry">Content Entry</Radio>
                      <Radio value="navigation">Navigation</Radio>
                      
                    </Stack>
                  </RadioGroup>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Choose what type of content you want to test
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{getEntityLabel(formData.entity_type)} to Test</FormLabel>
                  <Select
                    value={formData.entity_id}
                    onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                    placeholder={`Select a ${formData.entity_type}`}
                  >
                    {entities.map((entity) => (
                      <option key={entity.id} value={entity.id}>
                        {getEntityDisplayName(entity)}
                      </option>
                    ))}
                  </Select>
                  {entities.length === 0 && (
                    <Text fontSize="sm" color="orange.500" mt={2}>
                      No {formData.entity_type}s found. Please create some {formData.entity_type}s first.
                    </Text>
                  )}
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Test Settings</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Goal Metric</FormLabel>
                  <Select
                    value={formData.goal_metric}
                    onChange={(e) => setFormData({ ...formData, goal_metric: e.target.value })}
                  >
                    <option value="conversion_rate">Conversion Rate</option>
                    <option value="click_through_rate">Click Through Rate</option>
                    <option value="bounce_rate">Bounce Rate</option>
                    <option value="time_on_page">Time on Page</option>
                    <option value="revenue_per_visitor">Revenue per Visitor</option>
                  </Select>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    The primary metric you want to optimize
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Traffic Split (%)</FormLabel>
                  <HStack>
                    <Box flex={1}>
                      <Text fontSize="sm" mb={2}>Variant A: {formData.traffic_split}%</Text>
                      <NumberInput
                        value={formData.traffic_split}
                        onChange={(_, value) => setFormData({ ...formData, traffic_split: value })}
                        min={10}
                        max={90}
                        step={5}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" mb={2}>Variant B: {100 - formData.traffic_split}%</Text>
                      <Input value={`${100 - formData.traffic_split}%`} isReadOnly />
                    </Box>
                  </HStack>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    How to split traffic between the two variants
                  </Text>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Variant Configuration</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6}>
                <Alert status="warning">
                  <AlertIcon />
                  <AlertDescription>
                    Variant configuration will be completed after the test is created. 
                    You'll be able to set up the specific changes for each variant in the test editor.
                  </AlertDescription>
                </Alert>

                <Box w="full">
                  <Heading size="sm" mb={3}>Variant A (Control)</Heading>
                  <Text color="gray.500" fontSize="sm">
                    This will be your original, unchanged content that serves as the baseline.
                  </Text>
                </Box>

                <Divider />

                <Box w="full">
                  <Heading size="sm" mb={3}>Variant B (Treatment)</Heading>
                  <Text color="gray.500" fontSize="sm">
                    This will be your modified content that you want to test against the control.
                  </Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  );
};

export default CreateABTest;